// Rules of the sidebar: the menu is built from the user's rights cache (App Menu screens: name, group, order, icon, route)
// and the sidebar groups (order, icon, top level). Screens the user cannot view are left out; a group with no screen left
// is dropped. Top-level groups (e.g. Operations) show their screens one by one, mixed with the groups in sidebar order.

export const DEFAULT_ICON = "fas fa-circle";
export const DEFAULT_GROUP_ICON = "fas fa-folder";
export const COLLAPSED_KEY = "sidebar_collapsed";
/** Below this width the sidebar slides in over the page (same breakpoint as the layout CSS). */
export const MOBILE_MAX = 1024;

/** Masters › App Menu: the SystemUser's own screen, not an App Menu row. */
export const APP_MENU_SCREEN = { menuCode: "app-menu", menuName: "App Menu", menuGroup: "Masters", route: "/app-menu", sortOrder: 395, icon: "fas fa-sitemap", canView: true };

/**
 * Used only when the rights could not be loaded (API down): the screens of the website before the sidebar was data-driven,
 * so nobody is left without a menu. Normally the menu comes from App Menu.
 */
export const FALLBACK = {
  groups: [
    { groupName: "Operations", sortOrder: 0, icon: "fas fa-th-large", isTopLevel: true },
    { groupName: "Masters", sortOrder: 300, icon: "fas fa-database", isTopLevel: false },
    { groupName: "Configuration", sortOrder: 400, icon: "fas fa-cogs", isTopLevel: false },
    { groupName: "Crew Feedback", sortOrder: 465, icon: "fas fa-comments", isTopLevel: false },
    { groupName: "Report", sortOrder: 500, icon: "fas fa-chart-bar", isTopLevel: false },
  ],
  menus: [
    ["dashboard", "Dashboard", "Operations", "/dashboard-overview", 10, "fas fa-chalkboard"],
    ["dispatch", "Crew Transfers", "Operations", "/dispatch", 20, "fas fa-car"],
    ["crew-activity", "Crew Activity", "Operations", "/crew-activity", 30, "fas fa-user"],
    ["tracking", "Tracking", "Operations", "/tracking", 40, "fas fa-globe-asia"],
    ["local-vendor", "Local Vendor", "Masters", "/local-vendor", 310, "fas fa-building"],
    ["crew-member", "Staff Members", "Masters", "/crew-member", 320, "fas fa-user-nurse"],
    ["crew-address", "Staff Address", "Masters", "/crew-address", 330, "fas fa-home"],
    ["airport", "Airport", "Masters", "/airport", 340, "fas fa-plane"],
    ["hotels", "Hotel", "Masters", "/hotels", 350, "fas fa-hotel"],
    ["user", "Users", "Masters", "/user", 360, "fas fa-user-circle"],
    ["activity-code", "Activity Code", "Masters", "/activity-code", 370, "fas fa-car-side"],
    ["activity-location", "Activity Location", "Masters", "/nonfly-activity-location", 380, "fas fa-street-view"],
    ["nonfly-transport-matrix", "Non Fly Trans Matrix", "Masters", "/nonfly-transport-matrix", 390, "fas fa-car-side"],
    ["transport-overview", "Transport Overview", "Configuration", "/config/transport-overview", 410, "fas fa-route"],
    ["transport-matrix", "Transport Matrix", "Configuration", "/config/transport-matrix", 420, "fas fa-hotel"],
    ["booking-hotel-priority", "Booking Hotel Priority", "Configuration", "/config/booking/hotelpriority", 430, "fas fa-users"],
    ["email-setting", "Email Setting", "Configuration", "/config/email", 440, "fas fa-envelope"],
    ["airline-setting", "Airline Setting", "Configuration", "/config/airline-setting", 450, "fas fa-sliders-h"],
    ["notification", "Alerts & Notifications", "Operations", "/notification", 460, "fas fa-envelope-open-text"],
    ["rating", "Rating", "Crew Feedback", "/rating", 470, "fas fa-star"],
    ["feedback", "Feedback", "Crew Feedback", "/feedback", 480, "fas fa-comment-dots"],
    ["report-cancel-hotel", "Cancel Hotel", "Report", "/report/cancel", 510, "fas fa-hotel"],
    ["report-stay-hotel", "Stay Hotel", "Report", "/report/stay", 520, "fas fa-hotel"],
    ["report-crew-registration", "Crew Registration", "Report", "/report/crew-registration", 540, "fas fa-user-check"],
  ].map(([menuCode, menuName, menuGroup, route, sortOrder, icon]) => ({ menuCode, menuName, menuGroup, route, sortOrder, icon, canView: true })),
};

const byOrder = (a, b) => a.sortOrder - b.sortOrder || String(a.label).localeCompare(String(b.label));

/**
 * The sidebar entries in order: { type: "item", key, label, icon, route } and { type: "group", key, label, icon, items }.
 * permissions = the rights cache (null → FALLBACK); systemUser adds Masters › App Menu.
 */
export function buildSidebar(permissions, { systemUser = false } = {}) {
  const source = permissions?.menus?.length ? permissions : FALLBACK;
  const groups = (permissions?.groups?.length ? permissions.groups : FALLBACK.groups) ?? [];
  const groupOf = (name) => groups.find((g) => g.groupName === name);
  const menus = [...source.menus, ...(systemUser ? [APP_MENU_SCREEN] : [])].filter(
    (m) => m.route && (source === FALLBACK || permissions?.isFullAccess || m.canView),
  );

  const entries = [];
  const grouped = new Map();
  menus.forEach((m) => {
    const item = { type: "item", key: m.menuCode, label: m.menuName, icon: m.icon || DEFAULT_ICON, route: m.route, sortOrder: m.sortOrder };
    const group = groupOf(m.menuGroup);
    if (!m.menuGroup || group?.isTopLevel) {
      entries.push(item);
      return;
    }
    if (!grouped.has(m.menuGroup)) {
      grouped.set(m.menuGroup, {
        type: "group",
        key: `group:${m.menuGroup}`,
        label: m.menuGroup,
        icon: group?.icon || DEFAULT_GROUP_ICON,
        // A group without an App Menu › Groups row goes after the known ones, at its first screen's place.
        sortOrder: group ? group.sortOrder : 10000 + m.sortOrder,
        items: [],
      });
    }
    grouped.get(m.menuGroup).items.push(item);
  });
  grouped.forEach((g) => entries.push({ ...g, items: [...g.items].sort(byOrder) }));
  return entries.sort(byOrder);
}

/** Is the route the current page (exact, or a sub page of it)? */
export const isActiveRoute = (route, pathname) => {
  const p = String(pathname || "").toLowerCase();
  const r = String(route || "").toLowerCase();
  return Boolean(r) && (p === r || p.startsWith(`${r}/`));
};

/** The group holding the current page ("" when it is a top-level item or unknown). */
export const activeGroupKey = (entries, pathname) =>
  entries.find((e) => e.type === "group" && e.items.some((i) => isActiveRoute(i.route, pathname)))?.key ?? "";

/** The current page's menu name (page title), or "". */
export function pageTitle(entries, pathname) {
  const items = entries.flatMap((e) => (e.type === "group" ? e.items : [e]));
  const hit = items.filter((i) => isActiveRoute(i.route, pathname)).sort((a, b) => b.route.length - a.route.length)[0];
  return hit?.label ?? "";
}
